import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { DigitalClientService } from '../../../core/services/digital-client.service';
import { TipoDocumento } from '../../../core/models/digital-client/TipoDocumento';
import { Departamento } from '../../../core/models/digital-client/Departamento';
import { Municipio } from '../../../core/models/digital-client/Municipio';
import { DataService } from '../../../core/services/data.service';
import { Subject, takeUntil, take, distinctUntilChanged, filter, Observable } from 'rxjs';
import { FindQuote } from '../../../core/models/leasing/FindQuote';
import { QuoteProperty } from '../../../core/models/leasing/QuoteProperty';
import { ARRENDAMIENTOS_INMUEBLE, DESTINOS_INMUEBLE, TIPOS_INMUEBLE } from '../../../core/models/PropertyTypes';
import { TIPOS_PERSONA } from '../../../core/models/TipoPersona';
import { ChargingComponent } from '../../../shared/components/charging/charging.component';
import { LeasingService } from '../../../core/services/leasing.service';
import { FindLinkQuotePersonJsonData, FindLinkQuotePersonJsonData2 } from '../../../core/models/leasing/FindLinkQuotePerson';
import { firstValueFrom, merge } from 'rxjs';
import { TenantQuoteData } from '../../../core/models/leasing/TenantQuoteData';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { Opportunity } from '../../../core/models/Opportunity';
import { ValidatePersonRiskRequest } from '../../../core/models/digital-client/ValidatePersonRisk';
import { UppercaseDirective } from '../../../directives/UppercaseDirective';

interface StepConfig {
  title: string;
  description: string;
}

interface ContactDataFormModel {
  personType: string | null;
  activityType: number | null;
  idDocumentType: number | null;
  documentNumber: string | null;
  firstName: string | null;
  secondName: string | null;
  firstLastName: string | null;
  secondLastName: string | null;
  address: string | null;
  phone: string | null;
  mobilePhone: string | null;
  email: string | null;
  idDepartment: number | null;
  idCity: number | null;
}

interface PropertyDataFormModel {
  address: string | null;
  idDepartment: number | null;
  idCity: number | null;
  idPropertyType: number | null;
  idPropertyDestination: number | null;
  idLeasingProperty: number | null;
  leaseFeeValue: string | null;
  administrationFee: string | null;
}

interface TenantDataFormModel {
  [key: string]: {
    mobilePhone: string | null;
    email: string | null;
  };
}

@Component({
  selector: 'app-basic-data',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ChargingComponent, ModalComponent, UppercaseDirective],
  templateUrl: './basic-data.component.html',
  styleUrls: ['./basic-data.component.scss']
})
export class BasicDataComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild(ChargingComponent) charging!: ChargingComponent;
  @ViewChild(ModalComponent) modal!: ModalComponent;

  steps: StepConfig[] = [];

  // Forms
  contactDataForm: FormGroup<{ [K in keyof ContactDataFormModel]: FormControl<ContactDataFormModel[K]> }>;
  propertyDataForm: FormGroup<{ [K in keyof PropertyDataFormModel]: FormControl<PropertyDataFormModel[K]> }>;
  tenantDataForm!: FormGroup<{ [K in keyof TenantDataFormModel]: FormControl<TenantDataFormModel[K]> }>;

  // Data from services
  quoteData: FindQuote | null = null;
  propertyQuoteData: QuoteProperty | null = null;

  // Control variables
  currentStep: number = 1;
  personType: string = 'n';
  tiposPersona = TIPOS_PERSONA;
  role: number | null = null;
  codeudorNumber: number = 0;
  documentTypes: TipoDocumento[] = [];
  departments: Departamento[] = [];
  contactMunicipalities: Municipio[] = [];
  propertyMunicipalities: Municipio[] = [];
  isSearchEnabled: boolean = true;
  showAdditionalFields: boolean = false;
  loginRole: number | null = null;
  isLinked: number | null = null;
  radicationMainRole: number | null = null;

  // Property types
  tiposInmueble = TIPOS_INMUEBLE;
  destinosInmueble = DESTINOS_INMUEBLE;
  arrendamientosInmueble = ARRENDAMIENTOS_INMUEBLE;

  constructor(
    private fb: FormBuilder,
    private digitalClientService: DigitalClientService,
    private dataService: DataService,
    private leasingService: LeasingService
  ) {
    this.contactDataForm = this.createContactDataForm();
    this.propertyDataForm = this.createPropertyDataForm();
    //this.tenantDataForm = this.createTenantDataForm();
  }

  ngOnInit() {
    this.role = this.dataService.getCurrentRole();
    this.loginRole = this.dataService.getLoginRole();
    this.isLinked = this.dataService.getLinked() ?? 0;
    this.radicationMainRole = this.dataService.getRadicationMainRole();

    // Primero obtenemos los datos de la cotización
    this.dataService.quoteData$.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(quoteData => {
      if (quoteData) {
        this.quoteData = quoteData;
        this.codeudorNumber = this.quoteData.inNumeroCodeudores;
        // Inicializamos los steps después de tener los datos de la cotización
        this.initializeSteps();
        this.tenantDataForm = this.createTenantDataForm();
        this.loadTenantData();
      }
    });

    this.initializeSubscriptions();
    this.loadInitialData();
    this.setupFormListeners();
  }

  private initializeSubscriptions() {
    this.dataService.quoteData$.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(quoteData => {
      if (quoteData) {
        this.quoteData = quoteData;
        const contactData = this.dataService.getQuoteContactData();
        if (contactData) {
          const currentContact = contactData.find(contact => contact.inIdRole === this.role);
          if (currentContact) {
            const matchingPersonType = TIPOS_PERSONA.find(tipo =>
              tipo.value.toLowerCase() === currentContact.strPersonType.toLowerCase()
            );
            if (matchingPersonType) {
              this.showAdditionalFields = true;
              this.personType = matchingPersonType.value.toLowerCase();
              this.loadDocumentTypes();

              // Cargar municipios solo si hay departamento
              if (currentContact.inCodDepartment) {
                this.loadMunicipalities(currentContact.inCodDepartment, 'contact');
                const municipalityControl = this.contactDataForm.get('idCity');
                if (municipalityControl) {
                  municipalityControl.enable();
                  municipalityControl.setValue(currentContact.inCodCity);
                }
              }

              this.contactDataForm.patchValue({
                personType: matchingPersonType.value,
                activityType: currentContact.inIdNaturalPersonType || 0,
                firstLastName: currentContact.strContactFirstLastName,
                idDocumentType: currentContact.inCodIdentificationType,
                documentNumber: currentContact.inContactIdentification,
                secondLastName: currentContact.strContactSecondLastName || '',
                firstName: currentContact.strContactFirstName || '',
                secondName: currentContact.strContactSecondName || '',
                address: currentContact.strContactAddress || '',
                idDepartment: currentContact.inCodDepartment,
                idCity: currentContact.inCodCity,
                phone: currentContact.strContactTelephoneNumber,
                mobilePhone: currentContact.strContactMobileTelephoneNumber || '',
                email: currentContact.strContactEmail || ''
              });

              const actividad = currentContact.inIdNaturalPersonType || 0; 
                const roleId = this.role;
                this.dataService.updateActivityTypeData({
                  roleId: roleId,
                  activityType: actividad
                });
            }
          }
        }
        // Restauramos la actualización de datos de propiedad aquí
        if (this.role === 2) {
          this.updatePropertyFormWithQuoteData();
        }
      }
    });

    if (this.role === 2) {
      this.dataService.propertyQuoteData$.pipe(
        take(1),
        takeUntil(this.destroy$)
      ).subscribe(async data => {
        if (data) {
          this.propertyQuoteData = data;
        }

        this.updatePropertyFormWithQuoteData();

        // Determinar qué datos de departamento/municipio usar
        const departmentId = this.propertyQuoteData?.inCodPropertyDeparment || this.quoteData?.inIdDepartamento;
        const municipalityId = this.propertyQuoteData?.inCodPropertyMunicipality || this.quoteData?.inIdMunicipio;

        if (departmentId) {
          const response = await firstValueFrom(
            this.digitalClientService.GetMunicipiosPorDepartamento(
              departmentId.toString()
            )
          );

          if (response?.value) {
            this.propertyMunicipalities = response.value;
            const municipioControl = this.propertyDataForm.get('idCity');
            if (municipioControl && municipalityId) {
              municipioControl.enable();
              municipioControl.setValue(municipalityId);
            }
          }
        }
      });
    }
  }

  private updatePropertyFormWithQuoteData() {
    if (!this.quoteData) return;

    const values: any = {};

    if (this.propertyQuoteData) {
      // Si hay datos de propiedad, usar esos primero
      values.address = this.propertyQuoteData.strPropertyAdress;
      values.idDepartment = this.propertyQuoteData.inCodPropertyDeparment;
      values.idCity = this.propertyQuoteData.inCodPropertyMunicipality;
      values.idPropertyType = this.propertyQuoteData.inCodPropertyType;
      values.idPropertyDestination = this.propertyQuoteData.inCodPropertyDestination;
      values.idLeasingProperty = this.propertyQuoteData.inCodLeasingProperty;
      values.leaseFeeValue = this.formatNumberValue(this.propertyQuoteData.dcLeaseFeeValue);
      values.administrationFee = this.formatNumberValue(this.propertyQuoteData.dcAdministrationFee);
    } else {
      // Si no hay datos de propiedad, usar datos de la cotización
      values.address = this.quoteData.strContactAddress;
      values.idDepartment = this.quoteData.inIdDepartamento;
      values.idCity = this.quoteData.inIdMunicipio;
      values.idPropertyDestination = this.quoteData.inTypeHousing;
      values.leaseFeeValue = this.formatNumberValue(this.quoteData.dcLeaseFeeValue);
      values.administrationFee = this.formatNumberValue(this.quoteData.dcAdministrationFee);
    }

    // Actualizar el formulario con los valores recopilados
    this.propertyDataForm.patchValue(values);

    // Si hay departamento seleccionado, cargar los municipios correspondientes
    // const departmentId = values.idDepartment;
    // if (departmentId) {
    //   const municipioControl = this.propertyDataForm.get('idCity');
    //   if (municipioControl) {
    //     municipioControl.enable();
    //     this.loadMunicipalities(departmentId, 'property');
    //   }
    // }
  }

  private async loadInitialData() {
    this.loadDocumentTypes();

    const deptResponse = await firstValueFrom(this.digitalClientService.GetDepartamentosByPais());
    if (deptResponse?.value) {
      this.departments = deptResponse.value;
    }
  }

  private setupFormListeners() {
    merge(
      this.contactDataForm.get('idDocumentType')!.valueChanges,
      this.contactDataForm.get('documentNumber')!.valueChanges,
      this.contactDataForm.get('firstLastName')!.valueChanges
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateSearchEnabled();
    });

    this.contactDataForm.get('personType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.personType = value?.toLowerCase() || 'n';
        this.loadDocumentTypes();
        this.resetForm();
      });

    // Listener para departamento en formulario de contacto
    this.contactDataForm.get('idDepartment')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(), // Solo emite si el valor realmente cambia
        filter(value => value !== null) // Ignorar valores nulos
      )
      .subscribe(async value => {
        const municipalityControl = this.contactDataForm.get('idCity');
        if (municipalityControl) {
          municipalityControl.enable();
          municipalityControl.setValue(null);
          await this.loadMunicipalities(value.toString(), 'contact');
        }
      });

    // Solo configurar el listener para propiedad si es rol propietario
    if (this.role === 2) {
      this.propertyDataForm.get('idDepartment')?.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged(),
          filter(value => value !== null)
        )
        .subscribe(async value => {
          const municipioControl = this.propertyDataForm.get('idCity');
          if (municipioControl) {
            municipioControl.enable();
            municipioControl.setValue(null);
            await this.loadMunicipalities(value.toString(), 'property');
          }
        });
    }
  }

  private createContactDataForm(): FormGroup<{ [K in keyof ContactDataFormModel]: FormControl<ContactDataFormModel[K]> }> {
    const form = this.fb.group({
      personType: new FormControl<string | null>(this.tiposPersona[0].value, { validators: Validators.required }),
      activityType: new FormControl<number | null>(0),
      idDocumentType: new FormControl<number | null>(null, { validators: Validators.required }),
      documentNumber: new FormControl<string | null>('', { validators: Validators.required }),
      firstLastName: new FormControl<string | null>('', { validators: Validators.required }),
      secondLastName: new FormControl<string | null>(''),
      firstName: new FormControl<string | null>('', { validators: Validators.required }),
      secondName: new FormControl<string | null>(''),
      address: new FormControl<string | null>('', { validators: Validators.required }),
      phone: new FormControl<string | null>('', { validators: Validators.required }),
      mobilePhone: new FormControl<string | null>('', { validators: Validators.required }),
      email: new FormControl<string | null>('', {
        validators: [
          Validators.required,
          Validators.email,
          Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')
        ]
      }),
      idDepartment: new FormControl<number | null>(null, { validators: Validators.required }),
      idCity: new FormControl<number | null>({ value: null, disabled: true }, { validators: Validators.required })
    });

    // Suscribirse a cambios en personType para ajustar validaciones
    form.get('personType')?.valueChanges.subscribe(value => {
      const isNatural = value?.toLowerCase() === 'n';
      const firstNameControl = form.get('firstName');
      if (firstNameControl) {
        if (isNatural) {
          firstNameControl.setValidators([Validators.required]);
        } else {
          firstNameControl.clearValidators();
          firstNameControl.setValue('');
        }
        firstNameControl.updateValueAndValidity();
      }

      // Para persona jurídica, firstLastName es razón social
      const firstLastNameControl = form.get('firstLastName');
      if (firstLastNameControl) {
        // Mantener required pero actualizar los mensajes de error en el template
        firstLastNameControl.updateValueAndValidity();
      }
    });

    return form;
  }

  private createPropertyDataForm(): FormGroup<{ [K in keyof PropertyDataFormModel]: FormControl<PropertyDataFormModel[K]> }> {
    return this.fb.group({
      address: new FormControl<string | null>(null, { validators: Validators.required }),
      idDepartment: new FormControl<number | null>(null, { validators: Validators.required }),
      idCity: new FormControl<number | null>({ value: null, disabled: true }, { validators: Validators.required }),
      idPropertyType: new FormControl<number | null>(null, { validators: Validators.required }),
      idPropertyDestination: new FormControl<number | null>(null, { validators: Validators.required }),
      idLeasingProperty: new FormControl<number | null>(null, { validators: Validators.required }),
      leaseFeeValue: new FormControl<string | null>(null, { validators: [Validators.required, Validators.min(0)] }),
      administrationFee: new FormControl<string | null>(null, { validators: [Validators.required, Validators.min(0)] })
    });
  }

  private createTenantDataForm(): FormGroup {
    const formGroup: { [key: string]: FormGroup } = {};

    if (this.role === 1) { // Arrendatario
      // Para arrendatario vinculando propietario
      if (this.radicationMainRole === 1 && this.isLinked === 0) {
        formGroup['vinculado'] = this.fb.group({
          mobilePhone: ['', Validators.required],
          email: ['', [
            Validators.required,
            Validators.email,
            Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')
          ]]
        });
      }

      // Siempre agregar codeudores si existen
      if (this.quoteData?.inNumeroCodeudores) {
        for (let i = 0; i < this.quoteData.inNumeroCodeudores; i++) {
          formGroup[`codeudor${i}`] = this.fb.group({
            mobilePhone: ['', Validators.required],
            email: ['', [
              Validators.required,
              Validators.email,
              Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')
            ]]
          });
        }
      }
    }
    // Para propietario vinculando arrendatario
    else if (this.role === 2) { // Propietario
      if (this.radicationMainRole === 2 && this.isLinked === 0) {
        formGroup['vinculado'] = this.fb.group({
          mobilePhone: ['', Validators.required],
          email: ['', [
            Validators.required,
            Validators.email,
            Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')
          ]]
        });
      }
    }

    return this.fb.group(formGroup);
  }

  private resetForm() {
    this.showAdditionalFields = false;
    const resetValues: Partial<ContactDataFormModel> = {
      idDocumentType: null,
      documentNumber: '',
      firstLastName: '',
      secondLastName: '',
      firstName: '',
      secondName: '',
      address: '',
      phone: '',
      mobilePhone: '',
      email: '',
      idDepartment: null,
      idCity: null
    };
    this.contactDataForm.patchValue(resetValues);
  }

  loadDocumentTypes() {
    const personTypeNumber = this.personType === 'n' ? 1 : 2;
    this.digitalClientService.GetTiposDocumentoConParametro(personTypeNumber)
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.documentTypes = response.value;
          }
        },
        error: (error) => console.error('Error loading document types:', error)
      });
  }

  loadDepartments() {
    this.digitalClientService.GetDepartamentosByPais()
      .subscribe({
        next: (response) => {
          if (response.value) {
            this.departments = response.value;
          }
        },
        error: (error) => console.error('Error loading departments:', error)
      });
  }

  // Modificar el método loadMunicipalities para manejar diferentes casos
  private loadMunicipalities(departmentId: number | string, formType: 'contact' | 'property'): void {
    const seekId = departmentId.toString();

    // Verificar si ya tenemos los municipios cargados para este departamento
    const currentMunicipalities = formType === 'contact'
      ? this.contactMunicipalities.filter(m => m.codigoDepartamento.toString() === seekId)
      : this.propertyMunicipalities.filter(m => m.codigoDepartamento.toString() === seekId);

    // Si ya tenemos los municipios para este departamento, no hacer la llamada
    if (currentMunicipalities.length > 0) {
      return;
    }

    this.digitalClientService.GetMunicipiosPorDepartamento(seekId)
      .subscribe({
        next: (response) => {
          if (response.value) {
            if (formType === 'contact') {
              this.contactMunicipalities = response.value;
            } else {
              this.propertyMunicipalities = response.value;
            }
          }
        },
        error: (error) => console.error('Error loading municipalities:', error)
      });
  }

  searchPerson() {
    const formValues = this.contactDataForm.getRawValue();

    if (!formValues.idDocumentType || !formValues.documentNumber || !formValues.firstLastName) {
      return;
    }

    this.digitalClientService.FindUser(
      formValues.idDocumentType.toString(),
      formValues.documentNumber,
      formValues.firstLastName.toUpperCase()
    ).subscribe({
      next: (response) => {
        if (response.value.inSnVeto == 0) {
          this.showAdditionalFields = true;

          // Solo llamar a FindPersonFuccById si tenemos un inIdPersona válido
          if (response.value.inIdPersona && response.value.inIdPersona > 0) {
            this.digitalClientService.FindPersonFuccById(response.value.inIdPersona.toString())
              .subscribe({
                next: (fuccResponse) => {
                  if (fuccResponse.value?.datos_Basicos) {
                    this.contactDataForm.patchValue({
                      firstName: fuccResponse.value.datos_Basicos.primerNombre || '',
                      secondLastName: fuccResponse.value.datos_Basicos.segundoApellido || '',
                      secondName: fuccResponse.value.datos_Basicos.segundoNombre || '',
                      address: fuccResponse.value.datos_Basicos.numeroDireccionResidencia || '',
                      idDepartment: fuccResponse.value.datos_Basicos.codigoDepartamentoResidencia || null,
                      idCity: fuccResponse.value.datos_Basicos.codigoMunicipioResidencia || null,
                      phone: fuccResponse.value.datos_Basicos.telefonoDirecto || '',
                      mobilePhone: fuccResponse.value.datos_Basicos.telefonoCelular || '',
                      email: fuccResponse.value.datos_Basicos.email || ''
                    });
                  }
                },
                error: (error) => {
                  console.error('Error in FindPersonFuccById:', error);
                }
              });
          } else{
            this.showAdditionalFields = true; 
            this.modal.openModal(
              'Persona no encontrada',
              'La persona que se está buscando NO se encuentra registrada. Por favor diligencie la información manualmente.',
              false
            );
            this.clearAdditionalFields();
          }
        } else {
          this.showAdditionalFields = false;
          this.modal.openModal('Validación', 'El apellido diligenciado no coincide con el apellido registrado en el sistema.', false);
        }
      },
      error: (error) => {
        console.error('Error searching person:', error);
        this.modal.openModal('Ha ocurrido un error', 'Ha ocurrido un error en la consulta', false);
      }
    });
  }
  private clearAdditionalFields(): void {
    this.contactDataForm.patchValue({
      firstName: '',
      secondLastName: '',
      secondName: '',
      address: '',
      phone: '',
      mobilePhone: '',
      email: '',
      idDepartment: null,
      idCity: null
    });
    const municipioControl = this.contactDataForm.get('idCity');
    if (municipioControl) {
      municipioControl.disable();
    }
  }
  
  private async saveBasicData(role: number, isLastStep: boolean): Promise<void> {

    if (!isLastStep) return;

    const quotationNumber = this.dataService.getQuotationNumber();
    const quoteData = this.dataService.getQuoteData();
    const contactData = this.dataService.getQuoteContactData();
    const contactForm = this.contactDataForm.getRawValue();

    const existingContact = contactData?.find(contact => contact.inIdRole === role);
    const isJuridica = contactForm.personType?.toLowerCase() === 'j';

    const contactRequest = {
      inCodCity: Number(contactForm.idCity!),
      inCodDepartment: Number(contactForm.idDepartment!),
      dT_Birthday: new Date("1900-01-01").toISOString(),
      inCodBranchOffice: quoteData!.inCodSucursal,
      inCodIdentificationType: Number(contactForm.idDocumentType!),
      inContactIdentification: contactForm.documentNumber!,
      inIdAseguradoCD: 0,
      inIdNaturalPersonType: contactForm.activityType!, // 0 = Dependiente, 1 = Independiente, 2 = Pensionado
      inIdPersonCD: 0,
      inIdQuoteHeader: quotationNumber!,
      inIdRole: role,
      strContactAddress: contactForm.address!,
      strContactEmail: contactForm.email!,
      strContactFirstName: isJuridica ? 'S/N' : contactForm.firstName!,
      strContactSecondName: contactForm.secondName!,
      strContactFirstLastName: contactForm.firstLastName!,
      strContactSecondLastName: contactForm.secondLastName!,
      strContactMobileTelephoneNumber: contactForm.mobilePhone!,
      strContactTelephoneNumber: contactForm.phone!,
      strPersonType: contactForm.personType!
    };

    if (existingContact) {
      await firstValueFrom(this.leasingService.UpdateQuoteContact({
        ...contactRequest,
        inIdContactQuote: existingContact.inIdContactQuote
      }));
    } else {
      await firstValueFrom(this.leasingService.AddQuoteContact(contactRequest));
    }
  }

  private async saveLinkData(role: number): Promise<void> {
    const quotationNumber = this.dataService.getQuotationNumber();
    const formData = this.tenantDataForm.getRawValue();
    const tenantData = this.dataService.getTenantQuoteData(role) as TenantQuoteData;
    const updateData = tenantData != null ? true : false;

    // Obtener el JSON existente para preservar f1
    const data = await firstValueFrom(
      this.leasingService.FindLinkQuotePerson(quotationNumber!, role)
    );

    let linkJson: any = { f0: {}, f1: {} };

    // Si existe información previa, preservar f1
    if (data?.value?.strJson) {
      try {
        const existingJson = JSON.parse(data.value.strJson);
        // Preservar completamente f1 existente
        linkJson.f1 = existingJson.f1 || {};
      } catch (error) {
        console.error('Error parsing existing JSON:', error);
      }
    }

    // Actualizar solo f0 con los nuevos datos
    if (role === 1) { // Arrendatario
      const vinculadoData = formData['vinculado'];
      const codeudoresData = Object.keys(formData)
        .filter(key => key.startsWith('codeudor'))
        .map((key, index) => ({
          strCelular: formData[key]['mobilePhone'],
          strEmail: formData[key]['email']
        }));

      const isMainRole = this.radicationMainRole === 1;

      // Si tenemos datos del propietario (vinculadoData) y de codeudores
      if (vinculadoData && this.loginRole === 1 && this.isLinked === 0) {
        linkJson.f0 = {
          strCelular_v: vinculadoData.mobilePhone,
          strEmail_v: vinculadoData.email,
          rolprincipal: isMainRole,
          vincu: codeudoresData.length,
          vinculos: codeudoresData.length,
          vinculado: codeudoresData
        };
      } else { // Solo datos de codeudores
        linkJson.f0 = {
          vinculado: codeudoresData
        };
      }
    } else if (role === 2) { // Propietario
      const isMainRole = this.radicationMainRole === 2;
      
      linkJson.f0 = {
        strCelular_v: formData['vinculado']['mobilePhone'],
        strEmail_v: formData['vinculado']['email'],
        rolprincipal: isMainRole
      };
    }

    const linkRequest = {
      inIdRole: role,
      inIdQuoteHeader: quotationNumber!,
      strJson: JSON.stringify(linkJson),
      blUpdateLinkPerson: updateData
    };

    await firstValueFrom(this.leasingService.AddOrUpdateLinkQuotePerson(linkRequest));
  }

  private async savePropertyData(): Promise<void> {
    const quotationNumber = this.dataService.getQuotationNumber();
    const propertyData = this.dataService.getQuotePropertyData();
    const propertyFormValues = this.propertyDataForm.getRawValue();

    const propertyRequest = {
      inIdQuoteHeader: quotationNumber!,
      inCodPropertyDeparment: Number(propertyFormValues.idDepartment),
      inCodPropertyMunicipality: Number(propertyFormValues.idCity),
      strPropertyAdress: propertyFormValues.address,
      inCodPropertyType: Number(propertyFormValues.idPropertyType),
      inCodPropertyDestination: propertyFormValues.idPropertyDestination,
      strPropertyActivity: '',
      inCodLeasingProperty: Number(propertyFormValues.idLeasingProperty),
      dcLeaseFeeValue: parseFloat((propertyFormValues.leaseFeeValue || '0').replace(/,/g, '')),
      dcAdministrationFee: parseFloat((propertyFormValues.administrationFee || '0').replace(/,/g, '')),
      blUpdateProperty: !!propertyData
    };

     await firstValueFrom(this.leasingService.AddOrUpdatePropertyByQuoteHeader(propertyRequest));
  }

  async createOpportunityOwner() {
    const contactData = this.contactDataForm.value;
    const propertyData = this.propertyDataForm.value;
    const quote = this.dataService.getQuoteData();
    
    const opportunity: Opportunity = {
      opportunityId: quote?.uidOpportunityId?.toString() || '',
      actors: null,
      owner: {
        personType: contactData.personType === "N" ? 2 : 1,
        ownerType: contactData.personType === "N" ? 2 : 1,
        documentType: contactData.idDocumentType || 0,
        documentNumber: contactData.documentNumber || '',
        businessName: "",
        firstName: contactData.firstName || "",
        middleName: contactData.secondName || "",
        lastName: contactData.firstLastName || "",
        secondLastName: contactData.secondLastName || "",
        address: contactData.address || '',
        state: contactData.idDepartment || 0,
        city: contactData.idCity || 0,
        landline: contactData.phone || '',
        mobilePhone: contactData.mobilePhone || '',
        email: contactData.email || '',
        branch: 0,
        property: {
          address: propertyData.address || '',
          state: propertyData.idDepartment || 0,
          city: propertyData.idCity || 0,
          propertyType: propertyData.idPropertyType || 1,
          propertyPurpose: propertyData.idPropertyDestination || 1,
          propertyLease: propertyData.idLeasingProperty || 0,
          monthlyLease: parseFloat((propertyData.leaseFeeValue || '0').replace(/,/g, '')),
          administrationFee: parseFloat((propertyData.administrationFee || '0').replace(/,/g, ''))
        }
      }
    };

    await this.leasingService.sendOpportunity(opportunity).subscribe(result => {
      const response = result;
    })
  }


  async onSubmit() {
    if (!this.role) return;

    try {
      this.charging.showSpinner();
      if (this.currentStep === 1 && this.contactDataForm.valid) {
        if (this.role >= 3 || (this.role == 1 && this.codeudorNumber == 0 && this.radicationMainRole != 1)) {
          await this.saveBasicData(this.role, true);
          await this.reloadDataAfterSave();
          await this.dataService.changeView('home', null);
        } else {
          this.currentStep = 2;
        }
      }
      else if (this.currentStep === 2) {
        if (this.role === 2) {
          if (this.propertyDataForm.valid) {
            if (this.loginRole === 2 && !this.isLinked) {
              this.currentStep = 3;
            } else {            
              await this.saveBasicData(this.role, true);
              await this.savePropertyData();
              await this.createOpportunityOwner();
              await this.reloadDataAfterSave();
              await this.dataService.changeView('home', null);
            }
          }
        }
        else if (this.role === 1 && (this.tenantDataForm.valid || this.tenantDataForm.get('vinculado')?.valid)) {
          if (this.loginRole === 1 && this.isLinked === 0 && this.codeudorNumber > 0) {
            // Solo avanzar al siguiente step para vincular codeudores
            this.currentStep = 3;
          } else {
            await this.saveBasicData(this.role, true);
            await this.saveLinkData(this.role);
            await this.reloadDataAfterSave();
            await this.llenarCampos();
            await this.dataService.changeView('home', null);
          }
        }
      }
      else if (this.currentStep === 3 && this.tenantDataForm.valid) {
        await this.saveBasicData(this.role, true);
        await this.saveLinkData(this.role);
        if (this.role === 2) {
          await this.savePropertyData();
          await this.createOpportunityOwner();

          await this.llenarCampos();
        }
        await this.reloadDataAfterSave();
        await this.dataService.changeView('home', null);
      }
    } catch (error) {
      console.error('Error processing form submission:', error);
    } finally {
      this.charging.hideSpinner();
    }
  }


  async llenarCampos() {
    const form = this.contactDataForm.value;

    const request: ValidatePersonRiskRequest = {
      inDocumentType: Number(form.idDocumentType ?? 0),
      strDocumentNumber: form.documentNumber ?? '',
      strFirstSurname: form.firstLastName ?? '',
      strSecondSurname: form.secondLastName ?? '',
      strFirstName: form.firstName ?? '',
      strSecondName: form.secondName ?? ''
    };

    this.digitalClientService.ValidatePersonRisk(request).subscribe({
      next: (response) => {
      },
      error: (error) => {
        console.error('Error durante la validación:', error);
      }
    });
  }

  async goBack(): Promise<void> {
    await this.dataService.changeView('home', null);
  }

  private updateSearchEnabled() {
    const form = this.contactDataForm;
    this.isSearchEnabled = !!(
      form.get('idDocumentType')?.value &&
      form.get('documentNumber')?.value &&
      form.get('firstLastName')?.value
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatNumber(event: any) {
    let value = event.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    event.target.value = value;
  }

  private formatNumberValue(value: number): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  private loadTenantData() {
    if (!this.tenantDataForm) return;

    const tenantData = this.dataService.getTenantQuoteData() as TenantQuoteData;
    if (!tenantData) return;

    // Manejamos el caso de arrendatario (role 1)
    if (this.role === 1) {
      const arrendatarioData = tenantData[1 as keyof TenantQuoteData];

      // Si el loginRole es arrendatario y no está vinculado, cargamos datos del propietario
      if (this.loginRole === 1 && this.isLinked === 0 && arrendatarioData?.strCelular_v) {
        const vinculadoGroup = this.tenantDataForm.get('vinculado');
        if (vinculadoGroup) {
          vinculadoGroup.patchValue({
            mobilePhone: arrendatarioData.strCelular_v || '',
            email: arrendatarioData.strEmail_v || ''
          });
        }
      }

      // Cargar datos de codeudores si existen
      if (arrendatarioData?.vinculado && this.quoteData?.inNumeroCodeudores) {
        arrendatarioData.vinculado.forEach((codeudor, index) => {
          if (index < this.quoteData!.inNumeroCodeudores) {
            const codeudorGroup = this.tenantDataForm.get(`codeudor${index}`);
            if (codeudorGroup) {
              codeudorGroup.patchValue({
                mobilePhone: codeudor.strCelular || '',
                email: codeudor.strEmail || ''
              });
            }
          }
        });
      }
    }
    // Manejamos el caso de propietario (role 2)
    else if (this.role === 2) {
      const propietarioData = tenantData[2 as keyof TenantQuoteData];

      if (this.loginRole === 2 && propietarioData) {
        const vinculadoGroup = this.tenantDataForm.get('vinculado');
        if (vinculadoGroup) {
          vinculadoGroup.patchValue({
            mobilePhone: propietarioData.strCelular_v || '',
            email: propietarioData.strEmail_v || ''
          });
        }
      }
    }
  }

  private async reloadDataAfterSave(): Promise<void> {
    const quotationNumber = this.dataService.getQuotationNumber();
    if (!quotationNumber || !this.role) return;

    try {
      // Recargar datos de la cotización
      const quoteResponse = await firstValueFrom(
        this.leasingService.FindQuote(quotationNumber)
      );
      if (quoteResponse?.value) {
        this.dataService.updateFindQuoteData(quoteResponse.value);
      }

      // Recargar datos de la propiedad
      const propertyResponse = await firstValueFrom(
        this.leasingService.FindPropertyByQuoteHeader(quotationNumber)
      );
      if (propertyResponse?.value) {
        this.dataService.updateQuotePropertyData(propertyResponse.value);
      }

      // Recargar datos de contactos
      const contactsResponse = await firstValueFrom(
        this.leasingService.FindContactsByQuoteHeader(quotationNumber)
      );
      if (contactsResponse?.value) {
        this.dataService.updateQuoteContactData(contactsResponse.value);
      }

      // Recargar datos de vinculación
      const formData = this.tenantDataForm?.getRawValue();
      if (formData) {
        if (this.role === 1) {
          // Para arrendatario
          const tenantData: any = {
            vincu: 0,
            vinculos: 0,
            roleId: 1
          };

          // Si estamos vinculando propietario
          if (this.loginRole === 1 && this.isLinked === 0 && formData['vinculado']) {
            tenantData.strCelular_v = formData['vinculado'].mobilePhone || undefined;
            tenantData.strEmail_v = formData['vinculado'].email || undefined;
            tenantData.rolprincipal = true;
          }

          // Agregar datos de codeudores si existen
          const codeudores = Object.keys(formData)
            .filter(key => key.startsWith('codeudor'))
            .map(key => ({
              strCelular: formData[key].mobilePhone || undefined,
              strEmail: formData[key].email || undefined
            }));

          if (codeudores.length > 0) {
            tenantData.vincu = codeudores.length;
            tenantData.vinculos = codeudores.length;
            tenantData.vinculado = codeudores;
          }

          this.dataService.updateTenantQuoteData(1, tenantData);

        } else if (this.role === 2 && formData['vinculado']) {
          // Para propietario
          this.dataService.updateTenantQuoteData(2, {
            roleId: 2,
            strCelular_v: formData['vinculado'].mobilePhone || undefined,
            strEmail_v: formData['vinculado'].email || undefined,
            rolprincipal: true
          });
        }
      }

      // Recargar documentos
      const uploadedDocsResponse = await firstValueFrom(
        this.leasingService.FindQuoteDocumentsUploadedByIdRole(this.role, quotationNumber)
      );
      if (uploadedDocsResponse?.value) {
        this.dataService.updateDocumentsUploadedByRoleData(this.role, uploadedDocsResponse.value);
      }

      // Recargar documentos requeridos
      if (this.contactDataForm) {
        const idResponse = this.contactDataForm.get('personType')?.value === 'N' && this.role != 2 ? 1 : 0
        const requiredDocsResponse = await firstValueFrom(
          this.leasingService.FindDocumentsRequiredByTypePersonByRole(
            this.contactDataForm.get('personType')?.value || 'N',
            this.role,
            idResponse
          )
        );
        if (requiredDocsResponse?.value) {
          this.dataService.updateDocumentsRequiredByRoleData(this.role, requiredDocsResponse.value);
        }
      }

    } catch (error) {
      console.error('Error reloading data:', error);
    }
  }

  private initializeSteps(): void {
    const baseSteps: StepConfig[] = [
      {
        title: 'Datos Básicos',
        description: this.getStepOneDescription()
      }
    ];

    if (this.role === 2) {
      baseSteps.push({
        title: 'Datos del Inmueble',
        description: 'DILIGENCIE AQUÍ LOS DATOS DEL INMUEBLE A ARRENDAR'
      });

      if (this.loginRole === 2 && this.isLinked === 0) {
        baseSteps.push({
          title: 'Vinculación Arrendatario',
          description: 'DILIGENCIE AQUÍ LA INFORMACION DEL ARRENDATARIO PARA VINCULARLO A ESTE PROCESO'
        });
      }
    } else if (this.role === 1) {
      if (this.loginRole === 1 && this.isLinked === 0) {
        baseSteps.push({
          title: 'Vinculación Propietario',
          description: 'DILIGENCIE AQUÍ LA INFORMACION DEL PROPIETARIO DEL INMUEBLE PARA VINCULARLO A ESTE PROCESO'
        });
      }

      if (this.quoteData?.inNumeroCodeudores && this.quoteData.inNumeroCodeudores > 0) {
        baseSteps.push({
          title: 'Vinculación Codeudor(es)',
          description: this.getCodeudorStepDescription()
        });
      }
    }

    this.steps = baseSteps;
  }

  private getCodeudorStepDescription(): string {
    const count = this.quoteData?.inNumeroCodeudores || 0;
    return `DILIGENCIE AQUÍ LOS DATOS ${count === 1 ? 'DEL' : 'DE LOS'} 
      ${count === 1 ? 'CODEUDOR' : 'CODEUDORES'} 
      PARA VINCULARLO${count === 1 ? '' : 'S'} A ESTE PROCESO`;
  }

  private getStepOneDescription(): string {
    switch (this.role) {
      case 2:
        return 'DILIGENCIE AQUÍ LOS DATOS BASICOS DEL PROPIETARIO DEL INMUEBLE A ARRENDAR';
      case 1:
        return 'DILIGENCIE AQUÍ LOS DATOS BASICOS DEL ARRENDATARIO QUE VA A ARRENDAR EL INMUEBLE';
      default:
        return 'DILIGENCIE AQUÍ LOS DATOS BASICOS DEL CODEUDOR';
    }
  }

  getCodeudorArray(): number[] {
    return Array(this.quoteData?.inNumeroCodeudores || 0);
  }
}